import React from 'react';
import { Box, Grid, GridItem, Typography } from '@drone-js/core';
import { useSelector } from 'react-redux';
import { selectCmsFromBootstrap } from 'store/slice/bootstrap';
import CarouselImage from 'component/page/deprecatedDetail/DetailCard/ActivityCarousel/ActivityCarouselImage';
import { Image as ImageType } from 'gql/autogen/Image';
import { measureFinishRender } from 'core/Performance/PerformanceService';
import { selectPageTypeId } from 'store/slice/navigation';
import { selectScreenSize } from 'store/slice/screen';

interface MosaicProps {
    mosaicImages: ImageType[];
    onImageClick: (e: React.MouseEvent, index: number) => void;
    isLessThanGallerySize?: boolean;
    shouldShowTextOverlay?: boolean;
    onImagesLoaded?: () => void;
}

const SingleImage: React.FC<MosaicProps> = React.memo((props) => {
    return (
        <div data-testid="single-image-carousel">
            <CarouselImage image={props.mosaicImages[0]} imageIndex={1} totalImages={1} style={{ height: '360px' }} onLoad={props.onImagesLoaded} isMosaicEnabled={true} />
        </div>
    );
});
SingleImage.displayName = 'SingleImage';

const DoubleImage: React.FC<MosaicProps> = React.memo((props) => {
    return (
        <Grid data-testid="double-image-carousel" spacing="8" fluid={true}>
            {props.mosaicImages.map((image, index) => (
                <GridItem key={index} lg={6} md={6} sm={12}>
                    <Box
                        as="button"
                        interactive="base.inverted"
                        p="0"
                        borderWidth="1"
                        borderColor="base.default"
                        borderStyle="solid"
                        data-testid="activities-mosaic-gallery"
                        data-element-name="activities-mosaic-gallery"
                        data-element-index={index + 1}
                        borderRadius="8"
                        height="360px"
                        overflow="hidden"
                        onClick={(event) => props.onImageClick(event, index)}
                    >
                        <CarouselImage
                            image={image}
                            imageIndex={index + 1}
                            totalImages={props.mosaicImages.length}
                            style={{ height: '360px' }}
                            onLoad={props.onImagesLoaded}
                            isMosaicEnabled={true}
                        />
                    </Box>
                </GridItem>
            ))}
        </Grid>
    );
});
DoubleImage.displayName = 'DoubleImage';

const MultipleImage: React.FC<MosaicProps> = React.memo((props) => {
    const cms = useSelector(selectCmsFromBootstrap);
    const screenSize = useSelector(selectScreenSize);
    const mainImageHeight = screenSize.desktop ? '294px' : '244px';
    const thumbnailHeight = screenSize.desktop ? '143px' : '118px';

    const lastImagesWithTextOverlay = (index: number) => index === props.mosaicImages.length - 2 && props.shouldShowTextOverlay;
    const gridDynamicHeight = props.mosaicImages.length === 3 || (props.mosaicImages.length === 4 && screenSize.desktop);

    return (
        <Grid data-testid="multiple-image-carousel" spacing="8" fluid={true}>
            <GridItem lg={5} md={6} sm={12}>
                <Box
                    as="button"
                    interactive="base.inverted"
                    p="0"
                    borderWidth="1"
                    borderColor="base.default"
                    borderStyle="solid"
                    data-testid="activities-mosaic-gallery"
                    data-element-name="activities-mosaic-gallery"
                    data-element-index={1}
                    width="100%"
                    height={mainImageHeight}
                    borderRadius="8"
                    overflow="hidden"
                    onClick={(event) => props.onImageClick(event, 0)}
                >
                    <CarouselImage image={props.mosaicImages[0]} imageIndex={1} totalImages={props.mosaicImages.length} onLoad={props.onImagesLoaded} isMosaicEnabled={true} />
                </Box>
            </GridItem>
            <GridItem lg={7} md={6} sm={12}>
                <Grid spacing="8" fluid={true}>
                    {props.mosaicImages.slice(1).map((image, i) => (
                        <GridItem key={i} lg={4} md={6} sm={6}>
                            <Box position="relative" display="flex" alignItems="center" justifyContent="center" width="100%" height="100%">
                                <Box
                                    as="button"
                                    interactive="base.inverted"
                                    p="0"
                                    borderWidth="1"
                                    borderColor="base.default"
                                    borderStyle="solid"
                                    width="100%"
                                    height={gridDynamicHeight ? '100%' : thumbnailHeight}
                                    borderRadius="8"
                                    data-testid="activities-mosaic-gallery"
                                    data-element-name="activities-mosaic-gallery"
                                    data-element-index={i + 2}
                                    overflow="hidden"
                                    onClick={(event) => props.onImageClick(event, i + 1)}
                                >
                                    {!lastImagesWithTextOverlay(i) ? (
                                        <CarouselImage
                                            image={image}
                                            imageIndex={i + 2}
                                            totalImages={props.mosaicImages.length}
                                            onLoad={props.onImagesLoaded}
                                            isMosaicEnabled={true}
                                        />
                                    ) : (
                                        <>
                                            <CarouselImage
                                                image={image}
                                                imageIndex={i + 2}
                                                totalImages={props.mosaicImages.length}
                                                style={{ filter: 'brightness(0.5)' }}
                                                onLoad={props.onImagesLoaded}
                                                isMosaicEnabled={true}
                                            />
                                            <Box
                                                data-testid="show-all-photos-link"
                                                position="absolute"
                                                top="50%"
                                                left="50%"
                                                transform="translate(-50%, -50%)"
                                                width="100%"
                                                textAlign="center"
                                            >
                                                <Typography variant="body-s" textColor="base.inverted">
                                                    {cms[280689]}
                                                </Typography>
                                            </Box>
                                        </>
                                    )}
                                </Box>
                            </Box>
                        </GridItem>
                    ))}
                </Grid>
            </GridItem>
        </Grid>
    );
});
MultipleImage.displayName = 'MultipleImage';

const MosaicComponent: React.FC<MosaicProps> = React.memo((props) => {
    const pageTypeId = useSelector(selectPageTypeId);
    let imagesLoaded = 0;

    const onImagesLoaded = () => {
        imagesLoaded += 1;
        if (imagesLoaded >= props.mosaicImages.length) {
            // Send measurement when all images are loaded on landing
            measureFinishRender({ component: 'MosaicGallery' }, pageTypeId);
        }
    };

    switch (props.mosaicImages.length) {
        case 1: {
            return <SingleImage mosaicImages={props.mosaicImages} onImageClick={props.onImageClick} shouldShowTextOverlay={false} onImagesLoaded={onImagesLoaded} />;
        }
        case 2: {
            return <DoubleImage mosaicImages={props.mosaicImages} onImageClick={props.onImageClick} shouldShowTextOverlay={false} onImagesLoaded={onImagesLoaded} />;
        }
        default: {
            return (
                <MultipleImage
                    mosaicImages={props.mosaicImages}
                    onImageClick={props.onImageClick}
                    shouldShowTextOverlay={props.shouldShowTextOverlay}
                    onImagesLoaded={onImagesLoaded}
                    isLessThanGallerySize={props.isLessThanGallerySize}
                />
            );
        }
    }
});

MosaicComponent.displayName = 'MosaicComponent';
export default MosaicComponent;
